/**
 * JRS Mobile Menu
 * Complete JavaScript functionality for mobile navigation
 *
 * Features:
 * - Menu open/close with body scroll lock
 * - Level 1 to Level 2 transitions
 * - Accordion navigation with single-open behavior
 * - Focus trapping and keyboard navigation
 * - iOS Safari viewport fixes
 * - Reduced motion support
 *
 * Debug: Add ?mobileMenuDebug=1 to URL or run localStorage.setItem('mobileMenuDebug','1') then reload
 */

(function() {
  'use strict';

  // Debug mode: ?mobileMenuDebug=1 or localStorage.mobileMenuDebug
  function debugEnabled() {
    try {
      if (typeof window === 'undefined') return false;
      if (window.location.search.indexOf('mobileMenuDebug=1') !== -1) return true;
      return localStorage.getItem('mobileMenuDebug') === '1' || localStorage.getItem('mobileMenuDebug') === 'true';
    } catch (e) { return false; }
  }
  function debugLog() {
    if (!debugEnabled()) return;
    var args = ['[MobileMenu]'].concat(Array.prototype.slice.call(arguments));
    if (typeof console !== 'undefined' && console.log) console.log.apply(console, args);
  }
  function debugWarn() {
    if (!debugEnabled()) return;
    var args = ['[MobileMenu]'].concat(Array.prototype.slice.call(arguments));
    if (typeof console !== 'undefined' && console.warn) console.warn.apply(console, args);
  }
  function debugError() {
    var args = ['[MobileMenu]'].concat(Array.prototype.slice.call(arguments));
    if (typeof console !== 'undefined' && console.error) console.error.apply(console, args);
  }
  function debugAlwaysLog() {
    var args = ['[MobileMenu]'].concat(Array.prototype.slice.call(arguments));
    if (typeof console !== 'undefined' && console.log) console.log.apply(console, args);
  }

  // ==========================================================================
  // Utility Functions
  // ==========================================================================

  /**
   * Debounce function to limit execution rate
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Get focusable elements within a container
   */
  function getFocusableElements(container) {
    if (!container) return [];
    
    const focusableSelectors = [
      'a[href]:not([disabled]):not([tabindex="-1"])',
      'button:not([disabled]):not([tabindex="-1"])',
      'input:not([disabled]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled]):not([tabindex="-1"])',
      '[tabindex]:not([tabindex="-1"]):not([disabled])'
    ].join(', ');

    return Array.from(container.querySelectorAll(focusableSelectors))
      .filter(el => {
        // Only include visible elements
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && 
               style.visibility !== 'hidden' && 
               el.offsetParent !== null;
      });
  }

  /**
   * Check if user prefers reduced motion
   */
  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // ==========================================================================
  // Mobile Utility Slider (bottom section)
  // ==========================================================================

  class MobileMenuUtilitySlider {
    constructor(element) {
      this.el = element;
      this.track = element.querySelector('[data-mobile-utility-track]');
      this.slides = Array.from(element.querySelectorAll('[data-mobile-utility-slide]'));
      this.prevBtn = element.querySelector('[data-mobile-utility-prev]');
      this.nextBtn = element.querySelector('[data-mobile-utility-next]');

      this.autoplay = element.dataset.autoplay === 'true';
      this.interval = parseInt(element.dataset.autoplayInterval, 10) || 4000;
      this.pauseOnHover = element.dataset.pauseOnHover === 'true';
      this.animationDuration = parseInt(element.dataset.animationDuration, 10) || 400;
      this.animationEasing = element.dataset.animationEasing || 'ease-in-out';

      this.current = 0;
      this.timer = null;
      this.isPaused = false;
    }

    init() {
      if (!this.track || this.slides.length <= 1) return;
      this.bindEvents();
      if (this.autoplay && !prefersReducedMotion()) {
        this.startAutoplay();
      }
    }

    bindEvents() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.go(this.current - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.go(this.current + 1));

      if (this.pauseOnHover) {
        this.el.addEventListener('mouseenter', () => this.pause());
        this.el.addEventListener('mouseleave', () => this.resume());
      }
    }

    go(index) {
      if (!this.track || this.slides.length <= 1) return;
      const max = this.slides.length;
      const target = index < 0 ? max - 1 : index >= max ? 0 : index;
      const translate = -100 * target;
      this.track.style.transition = `transform ${this.animationDuration}ms ${this.animationEasing}`;
      this.track.style.transform = `translate3d(${translate}%, 0, 0)`;

      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === target);
      });

      this.current = target;
      if (this.autoplay && !this.isPaused) {
        this.startAutoplay();
      }
    }

    startAutoplay() {
      this.stopAutoplay();
      this.timer = setTimeout(() => {
        this.go(this.current + 1);
      }, this.interval);
    }

    stopAutoplay() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    }

    pause() {
      this.isPaused = true;
      this.stopAutoplay();
    }

    resume() {
      this.isPaused = false;
      if (this.autoplay) {
        this.startAutoplay();
      }
    }
  }

  function initMobileMenuUtilitySliders() {
    document.querySelectorAll('[data-mobile-utility-slider]').forEach((el) => {
      if (el._mobileUtilitySlider) return;
      const slider = new MobileMenuUtilitySlider(el);
      slider.init();
      el._mobileUtilitySlider = slider;
    });
  }

  // ==========================================================================
  // MobileMenu Class
  // ==========================================================================

  class MobileMenu {
    constructor(element) {
      debugLog('constructor called', 'element=', element);
      this.menu = element;

      if (!this.menu) {
        debugWarn('Menu element not found (null/undefined), skipping init');
        return;
      }

      // Cache DOM elements
      this.panel = this.menu.querySelector('[data-mobile-menu-panel]');
      this.overlay = this.menu.querySelector('[data-mobile-menu-overlay]');
      this.level1 = this.menu.querySelector('[data-mobile-menu-level="1"]');
      this.closeButtons = this.menu.querySelectorAll('[data-mobile-menu-close]');
      this.backButtons = this.menu.querySelectorAll('[data-mobile-menu-back]');
      this.submenuTriggers = this.menu.querySelectorAll('[data-mobile-menu-submenu-trigger]');
      this.submenus = this.menu.querySelectorAll('[data-mobile-menu-level="2"]');

      debugLog('DOM cache', {
        panel: !!this.panel,
        overlay: !!this.overlay,
        level1: !!this.level1,
        closeButtons: this.closeButtons ? this.closeButtons.length : 0,
        backButtons: this.backButtons ? this.backButtons.length : 0,
        submenuTriggers: this.submenuTriggers ? this.submenuTriggers.length : 0,
        submenus: this.submenus ? this.submenus.length : 0
      });

      // Get hamburger trigger from settings or default
      const hamburgerSelector = this.menu.dataset.hamburgerSelector || '[data-mobile-menu-toggle]';
      this.hamburgerButtons = document.querySelectorAll(hamburgerSelector);
      debugLog('hamburgerSelector=', hamburgerSelector, 'hamburgerButtons count=', this.hamburgerButtons ? this.hamburgerButtons.length : 0);

      // State
      this.isOpen = false;
      this.isAnimating = false;
      this.currentLevel = 1;
      this.currentSubmenuIndex = null;
      this.scrollPosition = 0;
      this.triggerElement = null;

      // Get animation duration from CSS custom property (safe: avoid null/undefined)
      let animationDuration = 300;
      try {
        const computedStyle = getComputedStyle(document.documentElement);
        const durationValue = (computedStyle.getPropertyValue('--mobile-menu-duration') || '').trim();
        const parsed = parseInt(durationValue, 10);
        if (!isNaN(parsed) && parsed >= 0) animationDuration = parsed;
      } catch (e) { /* use default */ }
      this.animationDuration = animationDuration;
      debugLog('animationDuration=', this.animationDuration);

      // Bind methods
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleResize = debounce(this.handleResize.bind(this), 250);
      this.handleOrientationChange = this.handleOrientationChange.bind(this);

      // Initialize
      this.init();
      debugLog('constructor finished');
    }

    // ========================================================================
    // Initialization
    // ========================================================================

    init() {
      debugLog('init() start');
      try {
        this.setVhProperty();
        debugLog('setVhProperty done');
        this.bindEvents();
        debugLog('bindEvents done');
        this.initAccordions();
        debugLog('initAccordions done');
      } catch (e) {
        debugError('init() error', e);
        throw e;
      }
      debugLog('init() finished');
    }

    bindEvents() {
      debugLog('bindEvents() start');
      const self = this;
      if (this.hamburgerButtons && this.hamburgerButtons.length) {
        this.hamburgerButtons.forEach(button => {
          if (!button || !button.addEventListener) return;
          button.addEventListener('click', (e) => {
            debugAlwaysLog('hamburger clicked');
            debugLog('hamburger clicked');
            e.preventDefault();
            e.stopPropagation();
            try {
              self.triggerElement = button;
              debugLog('calling open()');
              self.open();
              debugLog('open() returned');
            } catch (err) {
              debugError('hamburger click error', err);
            }
          });
        });
        debugLog('hamburger listeners attached:', this.hamburgerButtons.length);
      } else {
        debugWarn('no hamburger buttons to bind');
      }

      // Close buttons
      this.closeButtons.forEach(button => {
        button.addEventListener('click', () => this.close());
      });

      // Back buttons
      this.backButtons.forEach(button => {
        button.addEventListener('click', () => this.goBack());
      });

      // Overlay click
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // Submenu triggers (only chevron buttons, not the text links)
      this.submenuTriggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const index = parseInt(trigger.dataset.mobileMenuSubmenuIndex, 10);
          this.openSubmenu(index);
        });
      });

      // Window events
      window.addEventListener('resize', this.handleResize);
      window.addEventListener('orientationchange', this.handleOrientationChange);
    }

    // ========================================================================
    // iOS Safari Viewport Fix
    // ========================================================================

    setVhProperty() {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--mobile-menu-vh', `${vh}px`);
    }

    handleResize() {
      this.setVhProperty();
      
      // Close menu if resized above breakpoint
      if (window.innerWidth >= 1030 && this.isOpen) {
        this.close(true); // Force close without animation
      }
    }

    handleOrientationChange() {
      // Small delay to let the browser settle
      setTimeout(() => {
        this.setVhProperty();
      }, 100);
    }

    // ========================================================================
    // Menu Open/Close
    // ========================================================================

    open() {
      debugLog('open() entry', 'menu=', !!this.menu, 'isOpen=', this.isOpen, 'isAnimating=', this.isAnimating);
      if (!this.menu) {
        debugWarn('open() aborted: no menu');
        return;
      }
      if (this.isOpen || this.isAnimating) {
        debugLog('open() aborted: already open or animating');
        return;
      }

      try {
        debugLog('open() try block start');
        this.isAnimating = true;

        debugLog('open() setting scroll/body');
        this.scrollPosition = window.pageYOffset;
        document.body.style.setProperty('--scroll-position', `-${this.scrollPosition}px`);
        document.body.classList.add('mobile-menu-open');

        debugLog('open() ARIA + classList');
        this.menu.setAttribute('aria-hidden', 'false');
        if (this.hamburgerButtons && this.hamburgerButtons.length) {
          this.hamburgerButtons.forEach(btn => {
            if (btn && btn.setAttribute) btn.setAttribute('aria-expanded', 'true');
          });
        }

        this.menu.classList.add('is-open');
        this.isOpen = true;

        debugLog('open() dispatching mobileMenu:open');
        document.dispatchEvent(new CustomEvent('mobileMenu:open'));
        debugLog('open() after dispatch (listeners run)');

        document.addEventListener('keydown', this.handleKeydown);

        const animDuration = prefersReducedMotion() ? 0 : this.animationDuration;
        const self = this;
        debugLog('open() scheduling focus timeout', animDuration, 'ms');

        setTimeout(() => {
          debugLog('open() timeout: focus logic');
          self.isAnimating = false;
          try {
            const activeLevel = self.currentLevel === 1 ? self.level1 : self.getActiveSubmenu();
            if (activeLevel) {
              const focusableElements = getFocusableElements(activeLevel);
              if (focusableElements.length > 0 && focusableElements[0].focus) {
                focusableElements[0].focus();
              }
            }
          } catch (focusErr) {
            debugWarn('open() focus error', focusErr);
          }
          debugLog('open() timeout done');
        }, animDuration);
        debugLog('open() try block end (success)');
      } catch (err) {
        debugAlwaysLog('open() ERROR', err && err.message ? err.message : err);
        debugError('open() catch', err);
        this.isAnimating = false;
        this.isOpen = false;
        document.body.classList.remove('mobile-menu-open');
        document.body.style.removeProperty('--scroll-position');
      }
    }

    close(force = false) {
      if (!this.menu) return;
      if (!this.isOpen || (this.isAnimating && !force)) return;

      this.isAnimating = true;

      try {
        // Remove keyboard listener
        document.removeEventListener('keydown', this.handleKeydown);

        // Move focus out of the menu BEFORE hiding it from assistive tech: browsers
        // block aria-hidden on an element whose descendant (the close button) still
        // has focus. Return it to the element that opened the menu (usually the
        // hamburger); preventScroll keeps the scroll-locked page where it is.
        // The post-animation focus below stays as the fallback.
        const returnTarget = this.triggerElement || (this.hamburgerButtons && this.hamburgerButtons[0]);
        if (returnTarget && returnTarget.focus && this.menu.contains(document.activeElement)) {
          try {
            returnTarget.focus({ preventScroll: true });
          } catch (e) {
            returnTarget.focus();
          }
        }

        // Update ARIA
        this.menu.setAttribute('aria-hidden', 'true');
        if (this.hamburgerButtons && this.hamburgerButtons.length) {
          this.hamburgerButtons.forEach(btn => {
            if (btn && btn.setAttribute) btn.setAttribute('aria-expanded', 'false');
          });
        }

        // Hide menu - trigger close animation
        this.menu.classList.remove('is-open');
        this.isOpen = false;

        const animDuration = (prefersReducedMotion() || force) ? 0 : this.animationDuration;

        // Wait for animation to complete before unlocking scroll
        const self = this;
        setTimeout(() => {
          try {
            if (self.menu) self.resetToLevel1(true);
            requestAnimationFrame(() => {
              document.body.style.removeProperty('--scroll-position');
              document.body.classList.remove('mobile-menu-open');
              document.dispatchEvent(new CustomEvent('mobileMenu:close'));
              // The theme sets scroll-behavior: smooth on <html>, which turned this
              // restore into a visible scroll from the top back down to where the
              // shopper was. Force an instant jump, then put the style back.
              const rootStyle = document.documentElement.style;
              const previousBehavior = rootStyle.scrollBehavior;
              rootStyle.scrollBehavior = 'auto';
              window.scrollTo(0, self.scrollPosition);
              rootStyle.scrollBehavior = previousBehavior;
            });
            if (self.triggerElement && self.triggerElement.focus) {
              self.triggerElement.focus();
            }
            self.triggerElement = null;
          } catch (e) { /* no-op */ }
          self.isAnimating = false;
        }, animDuration);
      } catch (err) {
        this.isAnimating = false;
        document.body.classList.remove('mobile-menu-open');
        document.body.style.removeProperty('--scroll-position');
      }
    }

    // ========================================================================
    // Level Navigation
    // ========================================================================

    openSubmenu(index) {
      if (this.isAnimating || this.currentLevel === 2) return;

      const submenu = this.menu.querySelector(`[data-mobile-menu-submenu="${index}"]`);
      if (!submenu) return;

      this.isAnimating = true;
      this.currentSubmenuIndex = index;
      this.currentLevel = 2;

      // Update Level 1 trigger ARIA
      const trigger = this.menu.querySelector(`[data-mobile-menu-submenu-index="${index}"]`);
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      }

      // Hide Level 1
      if (this.level1) {
        this.level1.classList.add('is-hidden');
      }

      // Show Level 2
      submenu.classList.add('is-active');
      submenu.setAttribute('aria-hidden', 'false');

      const animDuration = prefersReducedMotion() ? 0 : this.animationDuration;

      setTimeout(() => {
        this.isAnimating = false;

        // Focus back button or first focusable
        const backButton = submenu.querySelector('[data-mobile-menu-back]');
        if (backButton) {
          backButton.focus();
        } else {
          const focusableElements = getFocusableElements(submenu);
          if (focusableElements.length > 0) {
            focusableElements[0].focus();
          }
        }

        // Initialize accordions (all collapsed; see applyDefaultAccordionState)
        this.initAccordionsInSubmenu(submenu);
      }, animDuration);
    }

    goBack() {
      if (this.isAnimating || this.currentLevel === 1) return;

      this.isAnimating = true;

      const activeSubmenu = this.getActiveSubmenu();

      // Move focus to the parent row BEFORE hiding the submenu from assistive tech.
      // Browsers block aria-hidden on an element whose descendant (the Back button)
      // still has focus ("Blocked aria-hidden" warning). preventScroll stops the
      // panel scrolling sideways towards the row while level 1 is still off-screen.
      if (this.currentSubmenuIndex !== null) {
        const parentRow = this.menu.querySelector(`[data-mobile-menu-submenu-index="${this.currentSubmenuIndex}"]`);
        if (parentRow && parentRow.focus) {
          try {
            parentRow.focus({ preventScroll: true });
          } catch (e) {
            parentRow.focus();
          }
        }
      }

      if (activeSubmenu) {
        // Hide Level 2
        activeSubmenu.classList.remove('is-active');
        activeSubmenu.setAttribute('aria-hidden', 'true');

        // Update trigger ARIA
        if (this.currentSubmenuIndex !== null) {
          const trigger = this.menu.querySelector(`[data-mobile-menu-submenu-index="${this.currentSubmenuIndex}"]`);
          if (trigger) {
            trigger.setAttribute('aria-expanded', 'false');
          }
        }
      }

      // Show Level 1
      if (this.level1) {
        this.level1.classList.remove('is-hidden');
      }

      this.currentLevel = 1;

      const animDuration = prefersReducedMotion() ? 0 : this.animationDuration;

      setTimeout(() => {
        this.isAnimating = false;

        // Focus the trigger that opened this submenu
        if (this.currentSubmenuIndex !== null) {
          const trigger = this.menu.querySelector(`[data-mobile-menu-submenu-index="${this.currentSubmenuIndex}"]`);
          if (trigger) {
            trigger.focus();
          }
        }
        
        this.currentSubmenuIndex = null;
      }, animDuration);
    }

    resetToLevel1(immediate = false) {
      // Close all submenus
      this.submenus.forEach(submenu => {
        submenu.classList.remove('is-active');
        submenu.setAttribute('aria-hidden', 'true');
      });

      // Reset all triggers
      this.submenuTriggers.forEach(trigger => {
        trigger.setAttribute('aria-expanded', 'false');
      });

      // Show Level 1
      if (this.level1) {
        this.level1.classList.remove('is-hidden');
      }

      this.currentLevel = 1;
      this.currentSubmenuIndex = null;

      // Reset all accordions (all collapsed; see applyDefaultAccordionState)
      if (!immediate) {
        this.resetAllAccordions();
      }
    }

    getActiveSubmenu() {
      return this.menu.querySelector('[data-mobile-menu-level="2"].is-active');
    }

    // ========================================================================
    // Accordion Functionality
    // ========================================================================

    initAccordions() {
      const allAccordions = this.menu.querySelectorAll('[data-mobile-menu-accordion]');
      
      allAccordions.forEach(accordion => {
        const trigger = accordion.querySelector('[data-mobile-menu-accordion-trigger]');
        
        if (trigger) {
          trigger.addEventListener('click', () => this.toggleAccordion(accordion));
        }
      });
    }

    initAccordionsInSubmenu(submenu) {
      const accordions = submenu.querySelectorAll('[data-mobile-menu-accordion]');
      
      if (accordions.length === 0) return;

      this.applyDefaultAccordionState(submenu, accordions);
    }

    // Default state for every submenu: all groups collapsed, the same for every
    // level-1 item. Card count, group count and the "Open first group by default in"
    // setting (data-mobile-menu-open-first) no longer open any group on entry.
    applyDefaultAccordionState(submenu, accordions) {
      accordions.forEach((accordion) => {
        this.closeAccordion(accordion);
      });
    }

    resetAllAccordions() {
      // Group accordions by their parent submenu
      this.submenus.forEach(submenu => {
        const accordions = submenu.querySelectorAll('[data-mobile-menu-accordion]');
        this.applyDefaultAccordionState(submenu, accordions);
      });
    }

    toggleAccordion(accordion) {
      const isExpanded = accordion.classList.contains('is-expanded');

      // Single-open behavior: close other accordions when opening one
      if (isExpanded) {
        this.closeAccordion(accordion);
      } else {
        // Close all other accordions in the same submenu first
        const parentSubmenu = accordion.closest('[data-mobile-menu-level="2"]');
        if (parentSubmenu) {
          const siblingAccordions = parentSubmenu.querySelectorAll('[data-mobile-menu-accordion]');
          siblingAccordions.forEach(sibling => {
            if (sibling !== accordion && sibling.classList.contains('is-expanded')) {
              this.closeAccordion(sibling);
            }
          });
        }
        this.openAccordion(accordion);
      }
    }

    openAccordion(accordion) {
      const trigger = accordion.querySelector('[data-mobile-menu-accordion-trigger]');
      const content = accordion.querySelector('.mobile-menu__accordion-content');
      
      if (!trigger || !content) return;

      accordion.classList.add('is-expanded');
      trigger.setAttribute('aria-expanded', 'true');
      content.removeAttribute('hidden');

      // Animate height
      if (!prefersReducedMotion()) {
        content.style.maxHeight = content.scrollHeight + 'px';
        
        // Remove max-height after animation to allow dynamic content
        setTimeout(() => {
          if (accordion.classList.contains('is-expanded')) {
            content.style.maxHeight = 'none';
          }
        }, this.animationDuration);
      } else {
        content.style.maxHeight = 'none';
      }
    }

    closeAccordion(accordion) {
      const trigger = accordion.querySelector('[data-mobile-menu-accordion-trigger]');
      const content = accordion.querySelector('.mobile-menu__accordion-content');
      
      if (!trigger || !content) return;

      // Set current height for animation
      if (!prefersReducedMotion()) {
        content.style.maxHeight = content.scrollHeight + 'px';
        
        // Force reflow
        content.offsetHeight;
        
        // Animate to 0
        content.style.maxHeight = '0';
      } else {
        content.style.maxHeight = '0';
      }

      accordion.classList.remove('is-expanded');
      trigger.setAttribute('aria-expanded', 'false');
      content.setAttribute('hidden', '');
    }

    // ========================================================================
    // Keyboard Navigation
    // ========================================================================

    handleKeydown(event) {
      // Escape closes menu
      if (event.key === 'Escape') {
        event.preventDefault();
        
        if (this.currentLevel === 2) {
          this.goBack();
        } else {
          this.close();
        }
        return;
      }

      // Tab for focus trap
      if (event.key === 'Tab') {
        this.trapFocus(event);
        return;
      }
    }

    trapFocus(event) {
      const activeContainer = this.currentLevel === 1 ? this.level1 : this.getActiveSubmenu();
      
      if (!activeContainer) return;

      const focusableElements = getFocusableElements(activeContainer);
      
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }

    // ========================================================================
    // Cleanup
    // ========================================================================

    destroy() {
      // Remove event listeners
      document.removeEventListener('keydown', this.handleKeydown);
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('orientationchange', this.handleOrientationChange);

      // Reset state
      if (this.isOpen) {
        this.close(true);
      }

      // console.log('MobileMenu: Destroyed');
    }
  }

  // ==========================================================================
  // Initialization
  // ==========================================================================

  function initMobileMenu() {
    debugLog('initMobileMenu() called, readyState=', document.readyState);
    const menuElement = document.querySelector('[data-mobile-menu]');

    if (!menuElement) {
      debugWarn('No [data-mobile-menu] element found - menu section may be missing');
      return;
    }
    debugLog('Menu element found', menuElement);

    try {
      debugLog('Creating MobileMenu instance');
      window.mobileMenu = new MobileMenu(menuElement);
      debugLog('Initializing utility sliders');
      initMobileMenuUtilitySliders();
      debugLog('initMobileMenu() completed successfully');
    } catch (error) {
      debugAlwaysLog('init ERROR', error && error.message ? error.message : error);
      debugError('Initialization failed', error);

      const hamburgers = document.querySelectorAll('[data-mobile-menu-toggle]');
      hamburgers.forEach(btn => {
        btn.addEventListener('click', () => {
          window.location.href = '/collections/all';
        });
      });
    }
  }

  debugLog('script loaded, scheduling init');
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      debugLog('DOMContentLoaded fired');
      initMobileMenu();
    });
  } else {
    initMobileMenu();
  }

  // Re-init sliders on section load (theme editor)
  document.addEventListener('shopify:section:load', initMobileMenuUtilitySliders);

})();

